import { css } from '@styled-system/css';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Message } from '@/shared/components/ui/Message';
import {
  getItemConditionLabel,
  isKnownItemCondition,
  localizeServerConditionTitle,
} from '@/shared/utils/analysis/itemCondition';

// ============================================================================
// Comparaison par caractéristique — % de vente + nombre de ventes, rien de
// plus. Deux blocs côte à côte : par état et par taille.
// ============================================================================

export interface CharacteristicStat {
  key: string;
  soldPct: number;
  sales: number;
}

interface CharacteristicCompareProps {
  conditions: CharacteristicStat[] | null;
  sizes: CharacteristicStat[] | null;
  loading?: boolean;
}

const row = css({
  display: 'flex',
  gap: '8',
  flexWrap: 'wrap',
});

const half = css({
  flex: 1,
  minWidth: '320px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const blockTitle = css({
  fontSize: 'body',
  fontWeight: 'semibold',
  color: 'text.primary',
});

const list = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
});

const itemRow = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(96px, 160px) 1fr auto',
  alignItems: 'center',
  gap: '4',
});

const itemLabel = css({
  fontSize: 'bodySm',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const barTrack = css({
  height: '8px',
  borderRadius: 'full',
  bg: 'surface.sunken',
  overflow: 'hidden',
});

const barFill = css({
  height: '100%',
  borderRadius: 'full',
  bg: 'brand.primary',
  transition: 'width 300ms ease',
});

const itemValues = css({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '2',
  whiteSpace: 'nowrap',
});

const pctValue = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  fontWeight: 'bold',
  color: 'text.primary',
});

const salesValue = css({
  fontFamily: 'mono',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const Block: FC<{ title: string; stats: CharacteristicStat[]; resolveLabel?: (key: string) => string }> = ({
  title,
  stats,
  resolveLabel,
}) => {
  const { t } = useTranslation();
  const sorted = [...stats].sort((a, b) => b.soldPct - a.soldPct);
  const maxPct = Math.max(1, ...sorted.map((s) => s.soldPct));

  return (
    <div className={half}>
      <span className={blockTitle}>{title}</span>
      <div className={list}>
        {sorted.map((stat) => (
          <div key={stat.key} className={itemRow}>
            <span className={itemLabel}>{resolveLabel ? resolveLabel(stat.key) : stat.key}</span>
            <div className={barTrack}>
              <div className={barFill} style={{ width: `${(stat.soldPct / maxPct) * 100}%` }} />
            </div>
            <span className={itemValues}>
              <span className={pctValue}>{Math.round(stat.soldPct)}%</span>
              <span className={salesValue}>· {t('statsPanel.tooltipSales', { count: stat.sales })}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CharacteristicCompare: FC<CharacteristicCompareProps> = ({ conditions, sizes, loading }) => {
  const { t } = useTranslation();

  if (loading || ((!conditions || conditions.length === 0) && (!sizes || sizes.length === 0))) {
    return (
      <Message type="info" bordered>
        {t('statsPanel.noActivity')}
      </Message>
    );
  }

  return (
    <div className={row}>
      {conditions && conditions.length > 0 && (
        <Block
          title={t('statsPanel.byCondition')}
          stats={conditions}
          // Le serveur renvoie le TITRE Vinted (déjà humain) — on ne traduit
          // que si la clé est un enum d'état (anciens formats), sinon on
          // affiche le titre tel quel.
          resolveLabel={(key) =>
            isKnownItemCondition(key) ? getItemConditionLabel(key) : localizeServerConditionTitle(key)
          }
        />
      )}
      {sizes && sizes.length > 0 && <Block title={t('statsPanel.bySize')} stats={sizes} />}
    </div>
  );
};
